import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  ChevronLeft,
  CircleAlert,
  KeyRound,
  Mail,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useIsMsp } from '@/hooks/useIsMsp';
import { usePermission } from '@/hooks/usePermission';
import { useConfirm } from '@/hooks/useConfirm';
import { useAvailableContexts } from '@/hooks/useContexts';
import { useActivateContext } from '@/hooks/useActivateContext';
import { useInvitations } from '@/hooks/useInvitations';
import {
  useCancelProvisioningToken,
  useCreateProvisioningToken,
  useProvisioningTokens,
  type ProvisioningToken,
} from '@/hooks/useProvisioningTokens';
import {
  useInviteCustomerAdmin,
  useMspCustomer,
  useMspCustomerIncidents,
  useUndesignateCustomer,
  useUpdateCustomer,
  useUpdateCustomerNotes,
} from '@/features/msp';
import type {
  MspCustomer,
  MspCustomerGateway,
  MspCustomerIncident,
  MspCustomerRegion,
  MspCustomerTag,
} from '@/types/mspCustomer';
import { RelativeTime } from '@/components/RelativeTime';
import { Markdown } from '@/components/Markdown';
import { ProvisioningTokenDialog } from '@/components/msp/ProvisioningTokenDialog';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { StatTile, type StatTone } from '@/components/ui/stat-tile';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { healthOf } from './mspDashboardModel';
import { SUSPENDED_LIFECYCLE, defaultLifecycle, lifecycleOption, pendingTokensFor, switchTargetsFor } from './mspCustomerDetailModel';


const HEALTH_PRESENTATION: Record<string, { tone: StatTone; label: string; icon: typeof CircleAlert; className: string }> = {
  critical: { tone: 'critical', label: 'Critical', icon: CircleAlert, className: 'bg-sev-1/15 text-sev-1' },
  warning: { tone: 'warning', label: 'Warning', icon: AlertTriangle, className: 'bg-sev-3/20 text-foreground' },
  healthy: { tone: 'good', label: 'Healthy', icon: CheckCircle2, className: 'bg-sev-4/15 text-sev-4' },
};

/** Health as an icon and a word, never colour alone. */
function HealthBadge({ status }: { status: string }) {
  const presentation = HEALTH_PRESENTATION[status] ?? {
    tone: 'neutral',
    label: status,
    icon: CircleAlert,
    className: 'bg-muted text-muted-foreground',
  };
  const Icon = presentation.icon;
  return (
    <Badge className={cn('border-0 font-normal gap-1 capitalize', presentation.className)}>
      <Icon className="w-3 h-3" aria-hidden="true" />
      {presentation.label}
    </Badge>
  );
}

/** A section of the page, so the panels don't each invent their own chrome. */
function Panel({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card">
      <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border-subtle">
        <h2 className="text-sm font-semibold">{title}</h2>
        {action}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

/**
 * A tag chip in the tag's own colour.
 *
 * `tag_values.color` is unvalidated free text. A non-hex value produces a CSS
 * declaration the browser ignores, which degrades to the default chip rather
 * than throwing — so no format guard is needed, only the awareness that one
 * isn't there.
 */
function TagChip({ tag }: { tag: MspCustomerTag }) {
  const label = tag.valueDisplayName ?? tag.value ?? tag.freeformValue ?? '—';
  return (
    <Badge variant="outline" className="font-normal gap-1.5">
      {tag.color ? (
        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: tag.color }} aria-hidden="true" />
      ) : null}
      <span className="text-muted-foreground">{tag.key}</span>
      <span>{label}</span>
    </Badge>
  );
}

export default function MSPCustomerDetails() {
  const { divisionId } = useParams<{ divisionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const organizationId = user?.organizationId;
  const isMsp = useIsMsp();
  const { confirm, confirmDialog } = useConfirm();

  const canManage = usePermission('msp_customer:manage');
  const canInvite = usePermission('invitation:create');
  // Issuing a token is how a gateway gets added, so it answers to gateway:create;
  // the separate token-create permission was retired for saying the same thing.
  const canCreateToken = usePermission('gateway:create');
  const canCancelToken = usePermission('gateway_provisioning_token:cancel');

  const { data, isLoading, isError, error, refetch, isFetching } = useMspCustomer(divisionId, organizationId, isMsp);
  const incidentsQuery = useMspCustomerIncidents(divisionId, organizationId, isMsp);

  const customer: MspCustomer | undefined = data?.customer;
  const rollup = customer?.rollup;
  const vocabulary = data?.vocabulary;

  const updateCustomer = useUpdateCustomer();
  const undesignate = useUndesignateCustomer();
  const updateNotes = useUpdateCustomerNotes();
  const inviteAdmin = useInviteCustomerAdmin();

  const { data: contexts } = useAvailableContexts(organizationId);
  const activateContext = useActivateContext();
  const { data: invitations } = useInvitations(organizationId);
  const { data: tokens } = useProvisioningTokens(organizationId, canCreateToken || canCancelToken);
  const createToken = useCreateProvisioningToken();
  const cancelToken = useCancelProvisioningToken();

  // --- notes ---------------------------------------------------------------
  //
  // A single markdown document, edited in place. `undefined` until the detail
  // request lands (the notes facet isn't in the placeholder row), `null` once we
  // know there are none.
  const storedNotes = data?.notes ?? null;

  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');

  const beginNotesEdit = () => {
    setNotesDraft(storedNotes?.content ?? '');
    setIsEditingNotes(true);
  };

  // --- token + invite sheets -----------------------------------------------
  const [isTokenSheetOpen, setIsTokenSheetOpen] = useState(false);
  const [tokenRegionId, setTokenRegionId] = useState('');
  const [tokenGatewayType, setTokenGatewayType] = useState('');
  const [tokenName, setTokenName] = useState('');
  const [issuedToken, setIssuedToken] = useState<ProvisioningToken | null>(null);

  const [isInviteSheetOpen, setIsInviteSheetOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  const regions = data?.regions;
  const gateways = data?.gateways;
  const tags = data?.tags;

  const switchTargets = useMemo(
    () => switchTargetsFor(divisionId ?? '', contexts),
    [divisionId, contexts],
  );
  const customerTokens = useMemo(() => pendingTokensFor(divisionId ?? '', tokens), [divisionId, tokens]);
  const administratorsTeamId = data?.administratorsTeamId;
  const pendingInvites = useMemo(
    () =>
      (invitations ?? []).filter(
        (i) => administratorsTeamId && i.teamId === administratorsTeamId && !i.acceptedAt && !i.isExpired,
      ),
    [invitations, administratorsTeamId],
  );

  // Rendered in the order the server returned: severity rank, then recency, then
  // id. Deliberately not re-sorted here — the endpoint's ordering is a stated
  // contract with a test behind it, and a second sort in the browser would be a
  // second definition of "worst first" free to drift from the first. Sorting by
  // severity alone would also only preserve the recency tiebreak by accident, via
  // sort stability.
  const incidents = incidentsQuery.data ?? [];

  if (!isMsp) {
    return (
      <PageContainer>
        <PageHeader title="Customer" />
        <EmptyState
          variant="no-data"
          title="MSP mode is off"
          description="Turn on MSP mode under Organization Admin → General to manage divisions as customers."
        />
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader title="Customer" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (isError || !customer) {
    const refusal = (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
    return (
      <PageContainer>
        <PageHeader title="Customer" />
        {refusal === 'not_a_customer' ? (
          <EmptyState
            variant="no-data"
            title="Not a customer"
            description="This division has not been designated as a customer."
            action={<Button onClick={() => void navigate('/customers')}>Back to customers</Button>}
          />
        ) : refusal === 'forbidden' ? (
          <EmptyState
            variant="no-data"
            title="You do not have access to MSP customers"
            description="This needs the MSP customer permission. An administrator can grant it."
          />
        ) : (
          <Alert variant="destructive">
            <AlertDescription>Could not load this customer: {error?.message ?? 'unknown error'}</AlertDescription>
          </Alert>
        )}
      </PageContainer>
    );
  }

  const status = healthOf(customer);
  const suspended = lifecycleOption(vocabulary?.lifecycle ?? [], SUSPENDED_LIFECYCLE);
  const reactivate = defaultLifecycle(vocabulary?.lifecycle ?? []);
  const isSuspended = customer.lifecycle === SUSPENDED_LIFECYCLE;

  const setLifecycle = (value: string, label: string) => {
    updateCustomer.mutate(
      { divisionId: customer.divisionId, lifecycle: value },
      {
        onError: (err: Error) => {
          const detail = (err as { response?: { data?: { description?: string } } }).response?.data;
          toast.error(`Could not ${label} this customer`, { description: detail?.description ?? err.message });
        },
      },
    );
  };

  const onSuspend = () => {
    if (!suspended) return;
    void (async () => {
      const ok = await confirm({
        title: `Suspend ${customer.name}?`,
        // Deliberately literal. Suspension is a label on the relationship and
        // nothing else — no polling stops, no devices pause, nothing about
        // billing changes. Copy implying otherwise would be false, and someone
        // would eventually rely on it.
        message:
          'This marks the customer as suspended for your own records. It does not stop monitoring, pause any devices, or change billing.',
        confirmText: 'Suspend',
      });
      if (ok) setLifecycle(suspended.value, 'suspend');
    })();
  };

  const onRemove = () => {
    void (async () => {
      const ok = await confirm({
        title: `Remove ${customer.name} from customers?`,
        message:
          'The division, its regions, devices, tier, account manager and notes are all kept — only the customer designation is removed. You can designate it again at any time.',
        confirmText: 'Remove',
      });
      if (!ok) return;
      undesignate.mutate(customer.divisionId, {
        onSuccess: () => {
          toast.success(`${customer.name} is no longer a customer`);
          void navigate('/customers');
        },
        onError: (err: Error) => { toast.error('Could not remove this customer', { description: err.message }); },
      });
    })();
  };

  const onSwitchContext = (contextId: string) => {
    activateContext.mutate(contextId, {
      onSuccess: () => { toast.success(`Switched to ${customer.name}`); },
    });
  };

  const saveNotes = () => {
    updateNotes.mutate(
      { divisionId: customer.divisionId, content: notesDraft },
      {
        onSuccess: () => {
          setIsEditingNotes(false);
          toast.success('Notes saved');
        },
        onError: (err: Error) => {
          const detail = (err as { response?: { data?: { description?: string } } }).response?.data;
          toast.error('Could not save notes', { description: detail?.description ?? err.message });
        },
      },
    );
  };

  const issueToken = () => {
    if (!organizationId || !tokenRegionId || !tokenGatewayType) return;
    createToken.mutate(
      {
        organizationId,
        divisionId: customer.divisionId,
        regionId: tokenRegionId,
        gateway_type: tokenGatewayType,
        name: tokenName.trim() === '' ? null : tokenName.trim(),
        description: null,
      },
      {
        onSuccess: (token) => {
          setIsTokenSheetOpen(false);
          setTokenName('');
          setIssuedToken(token);
        },
        onError: (err: Error) => {
          const detail = (err as { response?: { data?: { description?: string } } }).response?.data;
          toast.error('Could not issue a provisioning token', { description: detail?.description ?? err.message });
        },
      },
    );
  };

  const sendInvite = () => {
    inviteAdmin.mutate(
      { divisionId: customer.divisionId, email: inviteEmail },
      {
        onSuccess: (result) => {
          setIsInviteSheetOpen(false);
          setInviteEmail('');
          toast.success(`Invitation sent to ${result.email}`, { description: `They will join ${result.teamName}.` });
        },
        onError: (err: Error) => {
          const detail = (err as { response?: { data?: { description?: string } } }).response?.data;
          toast.error('Could not send the invitation', { description: detail?.description ?? err.message });
        },
      },
    );
  };

  const incidentColumns: readonly ResponsiveTableColumn<MspCustomerIncident>[] = [
    {
      key: 'severity',
      label: 'Severity',
      render: (i) => (
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{i.severity}</span>
      ),
    },
    {
      key: 'title',
      label: 'Incident',
      hideMobileLabel: true,
      render: (i) => (
        <Link to={`/incidents/${i.incidentId}`} className="font-medium hover:underline">
          {i.title}
        </Link>
      ),
    },
    { key: 'status', label: 'Status', hideAt: 'mobile', render: (i) => <span className="text-xs capitalize">{i.status}</span> },
    {
      key: 'createdAt',
      label: 'Opened',
      hideAt: 'mobile',
      render: (i) => <RelativeTime value={i.createdAt} className="text-xs text-muted-foreground" />,
    },
  ];

  const gatewayColumns: readonly ResponsiveTableColumn<MspCustomerGateway>[] = [
    { key: 'name', label: 'Gateway', hideMobileLabel: true, render: (g) => <span className="font-medium">{g.name ?? '—'}</span> },
    { key: 'status', label: 'Status', render: (g) => <span className="text-xs capitalize">{g.status}</span> },
    { key: 'region', label: 'Region', hideAt: 'mobile', render: (g) => <span className="text-xs">{g.regionName}</span> },
    {
      key: 'lastSeen',
      label: 'Last seen',
      hideAt: 'mobile',
      render: (g) =>
        g.lastSeen ? (
          <RelativeTime value={g.lastSeen} className="text-xs text-muted-foreground" />
        ) : (
          <span className="text-xs text-muted-foreground">never</span>
        ),
    },
  ];

  const regionColumns: readonly ResponsiveTableColumn<MspCustomerRegion>[] = [
    {
      key: 'name',
      label: 'Region',
      hideMobileLabel: true,
      render: (r) => (
        <Link to={`/admin/regions/${r.id}`} className="font-medium hover:underline">
          {r.name}
        </Link>
      ),
    },
  ];

  return (
    <PageContainer>
      <Link
        to="/customers"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
      >
        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
        Customers
      </Link>

      <PageHeader
        title={customer.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <HealthBadge status={status} />
            {customer.lifecycle ? (
              <Badge
                variant="outline"
                className="font-normal capitalize"
                style={customer.lifecycleColor ? { borderColor: customer.lifecycleColor } : undefined}
              >
                {customer.lifecycleDisplayName ?? customer.lifecycle}
              </Badge>
            ) : null}
            {customer.tier ? (
              <Badge variant="outline" className="font-normal capitalize">
                {customer.tierDisplayName ?? customer.tier}
              </Badge>
            ) : null}
            {customer.accountManager ? (
              <span className="text-xs text-muted-foreground">Account manager: {customer.accountManager}</span>
            ) : null}
          </span>
        }
        primary={
          <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching} className="gap-1.5">
            <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} aria-hidden="true" />
            Refresh
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatTile
          label="Devices"
          value={rollup?.deviceTotal ?? 0}
          detail={rollup ? `${String(rollup.monitoredDeviceCount)} monitored` : undefined}
        />
        <StatTile
          label="Gateways"
          value={rollup ? `${String(rollup.gatewaysOnline)}/${String(rollup.gatewayCount)}` : '0/0'}
          detail="online"
          tone={rollup && rollup.gatewaysOffline > 0 ? 'warning' : 'neutral'}
          toneLabel={rollup && rollup.gatewaysOffline > 0 ? 'some gateways offline' : 'all gateways online'}
        />
        <StatTile label="Regions" value={rollup?.regionCount ?? 0} />
        <StatTile
          label="Open incidents"
          value={rollup?.openIncidentCount ?? 0}
          detail={rollup?.worstOpenSeverity ? `worst: ${rollup.worstOpenSeverity}` : undefined}
          tone={rollup?.worstOpenSeverity === 'critical' ? 'critical' : rollup?.openIncidentCount ? 'warning' : 'neutral'}
          toneLabel={rollup?.worstOpenSeverity ?? 'no open incidents'}
        />
      </div>

      {rollup && rollup.healthIssues.length > 0 ? (
        <Alert className="mb-6">
          <AlertDescription>
            <ul className="space-y-1">
              {rollup.healthIssues.map((issue) => (
                <li key={issue.code} className="text-sm flex items-start gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{issue.message}</span>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-5">
          <Panel title="Open incidents">
            {incidentsQuery.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : incidentsQuery.isError ? (
              <p className="text-sm text-muted-foreground">
                Incidents could not be loaded for this customer. The rest of this page is unaffected.
              </p>
            ) : incidents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No open incidents.</p>
            ) : (
              <ResponsiveTable<MspCustomerIncident>
                columns={incidentColumns}
                rows={incidents}
                rowKey={(i) => i.incidentId}
              />
            )}
          </Panel>

          <Panel title="Gateways">
            {(gateways ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No gateways provisioned for this customer yet.</p>
            ) : (
              <ResponsiveTable<MspCustomerGateway> columns={gatewayColumns} rows={gateways ?? []} rowKey={(g) => g.id} />
            )}
          </Panel>

          <Panel title="Regions">
            {(regions ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No regions yet.</p>
            ) : (
              <ResponsiveTable<MspCustomerRegion> columns={regionColumns} rows={regions ?? []} rowKey={(r) => r.id} />
            )}
          </Panel>

          {/* Notes — the operator's runbook for this customer. A single markdown
              doc: who to call, the maintenance window, links out to tickets, the
              wiki, the contract in the MSP's own system. */}
          <Panel
            title="Notes"
            action={
              canManage && !isEditingNotes ? (
                <Button variant="outline" size="sm" onClick={beginNotesEdit}>
                  {storedNotes ? 'Edit' : 'Add notes'}
                </Button>
              ) : null
            }
          >
            {isEditingNotes ? (
              <div className="space-y-3">
                <Textarea
                  rows={12}
                  value={notesDraft}
                  onChange={(e) => { setNotesDraft(e.target.value); }}
                  placeholder={'## Runbook\n- Maintenance window: …\n- [Ticket](https://…)\n\nMarkdown supported.'}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Markdown supported. Link out to tickets, wikis, and the contract rather than copying them here.
                </p>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setIsEditingNotes(false);
                      setNotesDraft(storedNotes?.content ?? '');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={saveNotes} disabled={updateNotes.isPending}>
                    {updateNotes.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
            ) : storedNotes && storedNotes.content.trim() !== '' ? (
              <div className="space-y-2">
                <Markdown>{storedNotes.content}</Markdown>
                {storedNotes.updatedAt ? (
                  <p className="text-xs text-muted-foreground pt-2 border-t border-border-subtle">
                    Last edited <RelativeTime value={storedNotes.updatedAt} className="text-xs" />
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No notes yet.
                {canManage ? ' Add the runbook for this customer — contacts, maintenance windows, links out.' : ''}
              </p>
            )}
          </Panel>
        </div>

        <aside className="space-y-5">
          <Panel title="Quick actions">
            <div className="space-y-2">
              {/* Switch context. The customer's context is resolved from the
                  contexts this user can actually reach, so "not a member" is a
                  disabled control with a reason rather than a failed request. */}
              {switchTargets.length <= 1 ? (
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                  disabled={switchTargets.length === 0 || activateContext.isPending}
                  onClick={() => { if (switchTargets[0]) onSwitchContext(switchTargets[0].id); }}
                >
                  <ArrowRightLeft className="w-4 h-4" aria-hidden="true" />
                  Switch to this customer
                </Button>
              ) : (
                <Select onValueChange={onSwitchContext}>
                  <SelectTrigger>
                    <SelectValue placeholder="Switch to this customer…" />
                  </SelectTrigger>
                  <SelectContent>
                    {switchTargets.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {switchTargets.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  You are not a member of a team in this customer&apos;s context.
                </p>
              ) : null}

              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                disabled={!canCreateToken || (regions ?? []).length === 0}
                onClick={() => {
                  setTokenRegionId(regions?.length === 1 ? regions[0].id : '');
                  setIsTokenSheetOpen(true);
                }}
              >
                <KeyRound className="w-4 h-4" aria-hidden="true" />
                Issue provisioning token
              </Button>
              {!canCreateToken ? (
                <p className="text-xs text-muted-foreground">You do not have permission to add gateways.</p>
              ) : (regions ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">Create a region for this customer first.</p>
              ) : null}

              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                disabled={!canManage || !canInvite || !data?.administratorsTeamId}
                onClick={() => { setIsInviteSheetOpen(true); }}
              >
                <Mail className="w-4 h-4" aria-hidden="true" />
                Invite an administrator
              </Button>
              {!data?.administratorsTeamId ? (
                <p className="text-xs text-muted-foreground">
                  This customer has no Administrators team to invite into.
                </p>
              ) : !canInvite || !canManage ? (
                <p className="text-xs text-muted-foreground">You do not have permission to invite users.</p>
              ) : null}

              {canManage ? (
                <>
                  {isSuspended ? (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      disabled={!reactivate || updateCustomer.isPending}
                      onClick={() => { if (reactivate) setLifecycle(reactivate.value, 'reactivate'); }}
                    >
                      Return to {reactivate?.displayName ?? reactivate?.value ?? 'active'}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      disabled={!suspended || updateCustomer.isPending}
                      onClick={onSuspend}
                    >
                      Suspend
                    </Button>
                  )}
                  {!suspended && !isSuspended ? (
                    <p className="text-xs text-muted-foreground">
                      This organization&apos;s lifecycle catalog has no suspended state.
                    </p>
                  ) : null}
                  <Button variant="outline" className="w-full justify-start" onClick={onRemove} disabled={undesignate.isPending}>
                    Remove from customers
                  </Button>
                </>
              ) : null}
            </div>
          </Panel>

          {customerTokens.length > 0 ? (
            <Panel title="Pending tokens">
              <ul className="space-y-2">
                {customerTokens.map((token) => (
                  <li key={token.id} className="flex items-start justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <p className="truncate">{token.name ?? token.gatewayType}</p>
                      <p className="text-xs text-muted-foreground">
                        Expires <RelativeTime value={token.expiresAt} className="text-xs" />
                      </p>
                    </div>
                    {canCancelToken ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          void (async () => {
                            const ok = await confirm({
                              title: 'Cancel this token?',
                              message:
                                'A technician part-way through provisioning with it will have to start again — the token string cannot be recovered.',
                              confirmText: 'Cancel token',
                              isDanger: true,
                            });
                            if (ok) cancelToken.mutate(token.id);
                          })();
                        }}
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}

          {pendingInvites.length > 0 ? (
            <Panel title="Pending invitations">
              <ul className="space-y-1 text-sm">
                {pendingInvites.map((invite) => (
                  <li key={invite.id} className="flex items-center justify-between gap-2">
                    <span className="truncate">{invite.email}</span>
                    <RelativeTime value={invite.expiresAt} className="text-xs text-muted-foreground" />
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}

          <Panel title="Tags">
            {(tags ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No other tags. Lifecycle, tier and account manager are shown above.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(tags ?? []).map((tag) => (
                  <TagChip key={tag.resourceTagId} tag={tag} />
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Details">
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Customer since</dt>
                <dd>
                  <RelativeTime value={customer.designatedAt} absolute className="text-sm" />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Division created</dt>
                <dd>
                  <RelativeTime value={customer.createdAt} absolute className="text-sm" />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Slug</dt>
                <dd className="font-mono text-xs">{customer.slug}</dd>
              </div>
            </dl>
          </Panel>
        </aside>
      </div>

      <AppSheet
        open={isTokenSheetOpen}
        onOpenChange={(open) => { if (!open) setIsTokenSheetOpen(false); }}
        title="Issue a provisioning token"
      >
        <AppSheet.Body>
          <p className="text-sm text-muted-foreground mb-4">
            This adds a token. Any tokens already issued to this customer keep working.
          </p>
          <div className="space-y-3">
            <div>
              <Label htmlFor="token-region">Region</Label>
              <Select value={tokenRegionId} onValueChange={setTokenRegionId}>
                <SelectTrigger id="token-region">
                  <SelectValue placeholder="Choose a region" />
                </SelectTrigger>
                <SelectContent>
                  {(regions ?? []).map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="token-type">Gateway type</Label>
              <Select value={tokenGatewayType} onValueChange={setTokenGatewayType}>
                <SelectTrigger id="token-type">
                  <SelectValue placeholder="Choose a type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="appliance">Appliance</SelectItem>
                  <SelectItem value="vm">Virtual machine</SelectItem>
                  <SelectItem value="docker">Docker</SelectItem>
                </SelectContent>
              </Select>
              {/* The server decides expiry from the type and the two differ by
                  three orders of magnitude, so say so before the choice is made
                  rather than showing the consequence afterwards. */}
              <p className="text-xs text-muted-foreground mt-1">
                {tokenGatewayType === 'docker'
                  ? 'Docker tokens are long-lived.'
                  : tokenGatewayType
                    ? 'This token will expire in a few days.'
                    : 'Expiry depends on the gateway type.'}
              </p>
            </div>
            <div>
              <Label htmlFor="token-name">Name (optional)</Label>
              <Input id="token-name" value={tokenName} onChange={(e) => { setTokenName(e.target.value); }} />
            </div>
          </div>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button variant="ghost" onClick={() => { setIsTokenSheetOpen(false); }}>
            Cancel
          </Button>
          <Button onClick={issueToken} disabled={!tokenRegionId || !tokenGatewayType || createToken.isPending}>
            {createToken.isPending ? 'Issuing…' : 'Issue token'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <AppSheet
        open={isInviteSheetOpen}
        onOpenChange={(open) => { if (!open) setIsInviteSheetOpen(false); }}
        title={`Invite an administrator for ${customer.name}`}
      >
        <AppSheet.Body>
          <p className="text-sm text-muted-foreground mb-4">
            They will be invited into this customer&apos;s Administrators team.
          </p>
          <Label htmlFor="invite-email">Email</Label>
          <Input
            id="invite-email"
            type="email"
            value={inviteEmail}
            onChange={(e) => { setInviteEmail(e.target.value); }}
          />
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button variant="ghost" onClick={() => { setIsInviteSheetOpen(false); }}>
            Cancel
          </Button>
          <Button onClick={sendInvite} disabled={inviteEmail.trim() === '' || inviteAdmin.isPending}>
            {inviteAdmin.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      {issuedToken ? (
        <ProvisioningTokenDialog
          open
          onOpenChange={(open) => { if (!open) setIssuedToken(null); }}
          title={`Provisioning token for ${customer.name}`}
          token={issuedToken.token}
          tokenName={issuedToken.name}
          expiresAt={issuedToken.expiresAt}
        />
      ) : null}

      {confirmDialog}
    </PageContainer>
  );
}
